// Copyright (c) Docugami, Inc. All rights reserved.

import { BatchRequestContent, BatchRequestStep, BatchResponseContent, Client } from "@microsoft/microsoft-graph-client"
import { useEffect, useState } from "react";
import { Drive, Site, UsedInsight } from "@microsoft/microsoft-graph-types"
import { INavLinkGroup, Nav } from "@fluentui/react";
import styled from "styled-components";
import { appContentColor } from "components/shell/presentation/AppStyles";
import { GraphPickerSource } from "./GraphPicker";

const HeaderContainer = styled.h3`
    margin-left: 10px;
`

export interface GraphNavigationItem {
    siteId: string;
    name: string;
    drives: Partial<Drive>[];
    isPersonal?: boolean;
}

interface GraphNavigatorProps {
    client: Client;
    onItemSelected: (item: GraphNavigationItem) => void;
    defaultSource: GraphPickerSource;
    sourceFilter?: GraphPickerSource[];
}

export const GraphNavigator = (props: GraphNavigatorProps): JSX.Element => {
    const [selectedKey, setSelectedKey] = useState<string | undefined>();
    const [personalItem, setPersonalItem] = useState<GraphNavigationItem | null>(null);
    const [items, setItems] = useState<GraphNavigationItem[]>([]);

    useEffect(() => {
        initialize();
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        []);

    useEffect(() => {
        if (!selectedKey && !!personalItem && props.defaultSource === GraphPickerSource.OneDrive) {
            setSelectedKey(personalItem.siteId);
            props.onItemSelected(personalItem);
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [personalItem, selectedKey, props.defaultSource])

    useEffect(() => {
        if (!selectedKey && items.length > 0 && props.defaultSource === GraphPickerSource.SharePoint) {
            setSelectedKey(items[0].siteId);
            props.onItemSelected(items[0]);
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [items, selectedKey, props.defaultSource]);

    async function initialize() {
        const oneDriveEnabled = props.defaultSource === GraphPickerSource.OneDrive || props.sourceFilter?.includes(GraphPickerSource.OneDrive);
        const sharePointEnabled = props.defaultSource === GraphPickerSource.SharePoint || props.sourceFilter?.includes(GraphPickerSource.SharePoint);

        const initialSteps: BatchRequestStep[] = [];
        if (oneDriveEnabled) {
            // Fetch user drives if OneDrive is enabled
            initialSteps.push({
                id: "userDrives",
                request: new Request("/me/drives?$select=name,id")
            });
        }

        if (sharePointEnabled) {
            // Fetch all sites as well as recently visited sites if SharePoint is enabled
            initialSteps.push(
                {
                    id: "sites",
                    request: new Request("/sites?search=*&$select=displayName,id,drives,drive")
                },
                {
                    id: "recent",
                    request: new Request("/me/insights/used?$filter=ResourceVisualization/Type eq 'spsite'&$orderby=lastUsed/lastAccessedDateTime desc")
                });
        }

        const initialBatchRequestContent = new BatchRequestContent(initialSteps);
        const initialContent = await initialBatchRequestContent.getContent();
        const initialResponse = await props.client.api('/$batch').post(initialContent);
        const initialResponseContent = new BatchResponseContent(initialResponse);

        if (oneDriveEnabled) {
            // Set the user's personal drives
            const userDrives: Partial<Drive>[] = (await initialResponseContent.getResponseById("userDrives").json()).value as Partial<Drive>[];
            if (userDrives.length > 0) {
                setPersonalItem(
                    {
                        siteId: "userDrives",
                        name: "My Files",
                        drives: userDrives,
                        isPersonal: true
                    }
                )
            }
        }

        if (sharePointEnabled) {
            // Filter the list of all sites based on those that have been recently used
            const recent: Partial<UsedInsight>[] = (await initialResponseContent.getResponseById("recent").json()).value as Partial<UsedInsight>[];
            const userSites: Partial<Site>[] = (await initialResponseContent.getResponseById("sites").json()).value as Partial<Site>[];

            const targetSites: Partial<Site>[] = [];
            recent.forEach(x => {
                const index = userSites.findIndex(y => x.resourceReference!.id!.includes(y.id!) || y.id!.includes(x.resourceReference!.id!));
                if (index !== -1) {
                    const item = userSites.splice(index, 1)[0];
                    targetSites.push(item);
                }
            });

            const MAX_SITES = 10;
            targetSites.push(...userSites.slice(0, Math.max(0, MAX_SITES - targetSites.length - 1)));
            targetSites.sort((a,b) => a.displayName!.localeCompare(b.displayName!));

            const steps: BatchRequestStep[] = targetSites.map(site => {
                const request = new Request(`/sites/${site.id!}/drives?$select=id,name,driveType,owner`, { method: 'GET' });
                const step: BatchRequestStep = {
                    id: site.id!,
                    request: request
                }

                return step;
            });

            const batchRequestContent = new BatchRequestContent(steps);
            const content = await batchRequestContent.getContent();
            const response = await props.client.api('/$batch').post(content);
            const batchResponseContent = new BatchResponseContent(response);

            // Construct navigation items for each site and its associated drives
            const navItems: GraphNavigationItem[] = [];
            for (const site of targetSites) {
                let siteDrives = (await batchResponseContent.getResponseById(site.id!).json()).value as Partial<Drive>[] | undefined;
                siteDrives = siteDrives?.filter(x => x.driveType !== "personal" && x.driveType !== "business");
                if (!siteDrives?.length) {
                    continue;
                }

                const navItem: GraphNavigationItem = {
                    siteId: site.id!,
                    name: site.displayName!,
                    drives: siteDrives
                };

                navItems.push(navItem);
            }

            setItems(navItems);
        }
    }

    function onSelected(item: GraphNavigationItem) {
        setSelectedKey(item.siteId);
        props.onItemSelected(item);
    }

    const getNavLinkGroups = (): INavLinkGroup[] => {
        const navGroups: INavLinkGroup[] = [];
        if (!!personalItem && (!props.sourceFilter || props.sourceFilter.includes(GraphPickerSource.OneDrive))) {
            navGroups.push(
                {
                    name: personalItem.name,
                    links: [
                        {
                            name: 'My Files',
                            url: '',
                            key: personalItem.siteId,
                            onClick: () => onSelected(personalItem),
                            iconProps: {
                                iconName: "FabricFolder",
                                styles: {
                                    root: {
                                        color: appContentColor
                                    }
                                }
                            }
                        }
                    ]
                }
            )
        }

        if (!props.sourceFilter || props.sourceFilter.includes(GraphPickerSource.SharePoint)) {
            navGroups.push(
                {
                    name: 'Sites',
                    links: items.map(site => {
                        return {
                            name: site.name,
                            url: '',
                            key: site.siteId,
                            onClick: () => onSelected(site),
                            iconProps: {
                                iconName: "SharepointLogo",
                                styles: {
                                    root: {
                                        color: appContentColor
                                    }
                                }
                            }
                        }
                    })
                }
            );
        }

        return navGroups;
    }

    function renderGroupHeader(props?: INavLinkGroup, defaultRender?: (props?: INavLinkGroup) => JSX.Element | null): JSX.Element | null {
        if (!defaultRender) {
            return null;
        }

        return (
            <HeaderContainer>
                {props?.name ?? ''}
            </HeaderContainer>
        );
    }

    return (
        <Nav
            styles={{
                link: {
                    color: appContentColor,
                    ':hover:active': {
                        color: appContentColor
                    }
                }
            }}
            selectedKey={selectedKey}
            groups={getNavLinkGroups()}
            onRenderGroupHeader={renderGroupHeader} />
    );
}
